import MenuList from '@components/common/menu/MenuList';
import { buildContextMenuFromType } from '@components/map/contextMenu/builderFactory';
import { HasMeasureContext, HasQrCodeContext } from '@components/map/contextMenu/builders/common';
import { ReplayEventContextMenuContext } from '@components/map/contextMenu/builders/replayEvent';
import { FeatureTypeToContextComponentEnum } from '@components/map/contextMenu/type';
import { useMapContext } from '@components/map/MapContext';
import { MeasureOriginEnum, useMeasureFromPointMenuItem } from '@hooks/useMeasureFromPointMenuItem';
import { useWithDispatch } from '@hooks/useWithDispatch';
import { updatePopupByMapId } from '@redux/maps/maps.reducer';
import { toLngLat } from '@utils/validation/coordinates.utils';

import { MenuItems } from '@/types/menu.types';
import { PopupDataType, PopupTypeEnum } from '@/types/popup.types';
import { ReplayEvent } from '@/types/replay/replay.types';

interface EventContextMenuProps {
  replayEvent: ReplayEvent;
  clickPosition: { x: number; y: number };
  onClose: () => void;
}

export function ReplayEventContextMenu({ clickPosition, replayEvent, onClose }: Readonly<EventContextMenuProps>) {
  const { mapId } = useMapContext();

  const updatePopup = useWithDispatch(updatePopupByMapId);

  const handleUpdateReplayEvent = () => {
    updatePopup({
      mapId,
      popup: {
        type: PopupTypeEnum.EVENT,
        position: { x: clickPosition.x, y: clickPosition.y },
        data: {
          editedEvent: replayEvent,
        },
      },
    });
    onClose();
  };

  const openPopup = (popupDataType: PopupDataType) =>
    updatePopup({
      mapId,
      popup: {
        ...popupDataType,
        position: { x: clickPosition.x, y: clickPosition.y },
      },
    });

  const openGpsQrCodePopup = () => {
    if (replayEvent.position) {
      openPopup({
        type: PopupTypeEnum.GPS_QR_CODE,
        data: {
          coordinates: replayEvent.position,
        },
      });
      onClose();
    }
  };

  const [label, enableMeasure] = useMeasureFromPointMenuItem({
    closeContextMenu: onClose,
    position: replayEvent.position,
    measureOrigin: MeasureOriginEnum.EVENT,
  });

  const measureContext: HasMeasureContext = {
    position: toLngLat(replayEvent.position),
    label,
    enableMeasure,
  };
  const qrCodeContext: HasQrCodeContext = {
    position: toLngLat(replayEvent.position),
    openGpsQrCodePopup,
  };

  const ctx: ReplayEventContextMenuContext = {
    handleUpdateReplayEvent,
    ...measureContext,
    ...qrCodeContext,
  };

  const menuItems: MenuItems = buildContextMenuFromType<FeatureTypeToContextComponentEnum.REPLAY_EVENT>(
    FeatureTypeToContextComponentEnum.REPLAY_EVENT,
    mapId,
    ctx,
  );

  return <MenuList menuItems={menuItems} />;
}
